import { Money } from './money.model';

export type CollaboratorRole = 'developer' | 'designer' | 'graphic-designer' | 'qa' | 'pm';
export type CollaboratorStatus = 'active' | 'paused' | 'offboarded';

export type CollaboratorAvailability = 'full_time' | 'part_time' | 'unavailable';

export interface CollaboratorLink {
  label: string;
  url: string;
}

export interface CollaboratorProjectEntry {
  id: string;
  name: string;
  url?: string;
  description: string;
}

/** Payout destination. Never shown on the public profile, never to buyers.
 *  Admin reads it only inside an admin-authenticated callable response. */
export interface CollaboratorBankDetails {
  accountHolderName: string;
  accountNumber: string;
  ifsc: string;
  bankName?: string;
  upiId?: string;
}

export interface Collaborator {
  id: string;
  displayName: string;
  role: CollaboratorRole;

  /**
   * What Angularize pays this person per story point — their COST BASIS, in
   * integer paise. Never renderable to a buyer.
   *
   * OPTIONAL, and absent by default. A rate is entered in exactly two places:
   * the partner-application approval form, and the admin rate editor on the
   * roster. It is never set from a quick-add form and never has a default
   * value — a defaulted cost rate is a number nobody chose that quietly
   * becomes what somebody gets paid.
   *
   * A collaborator with no rate CANNOT be staffed onto a quote (computeQuote
   * skips them, and the team-assembly dropdown blocks them). That guard has
   * to live at quote time: if a rate-less person reaches a ticket, they can
   * do the work, mark it deployed, and then acceptWorkItem throws when the
   * BUYER presses Accept — work delivered, nobody payable.
   */
  payoutRatePerPoint?: Money;

  status: CollaboratorStatus;
  avatarUrl?: string;
  headline?: string;
  skills?: string[];
  rating?: number;
  ratingCount?: number;

  bio?: string;
  yearsExperience?: number;
  availability?: CollaboratorAvailability;

  /**
   * Internal contact — never shown to buyers or on the public profile.
   * Doubles as the CLAIM KEY for portal access: an admin-created record
   * starts with only this set (no uid), and the first login with a matching
   * auth email writes the uid onto it. See resolvePartnerAccess.
   *
   * REQUIRED at create() time. A record without one can never be linked to
   * the human who logs in, and silently becomes a second, orphaned copy of
   * that person the moment they apply as a partner.
   */
  email?: string;

  phone?: string;
  location?: string;
  links?: CollaboratorLink[];
  resumeUrl?: string;
  notableProjects?: CollaboratorProjectEntry[];
  bankDetails?: CollaboratorBankDetails;

  /** Firebase Auth uid. Unset for admin-added records until first login
   *  claims them by email. Always set for records born from an approved
   *  application. This is what partner-guard resolves against. */
  uid?: string;

  /** Stable public-profile slug. Generated ONCE, never regenerated — even
   *  if displayName changes. /execution-partner/:slug must keep working
   *  forever once shared. Sole lookup key for the public profile page. */
  publicSlug?: string;

  externalRefs?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

/** True when this person can actually be priced into a quote. */
export function hasPayoutRate(c: Collaborator): boolean {
  return !!c.payoutRatePerPoint && c.payoutRatePerPoint.amount > 0;
}

/** Their rate in paise, or 0 when unset. Callers that price work must check
 *  hasPayoutRate first — 0 is not a rate, it's a missing one. */
export function ratePaise(c: Collaborator): number {
  return c.payoutRatePerPoint?.amount ?? 0;
}

export interface CollaboratorUpdate {
  displayName?: string;
  role?: CollaboratorRole;
  payoutRatePerPoint?: Money;
  avatarUrl?: string | null;
  headline?: string | null;
  skills?: string[];
  bio?: string | null;
  yearsExperience?: number | null;
  availability?: CollaboratorAvailability | null;
  email?: string | null;
  phone?: string | null;
  location?: string | null;
  links?: CollaboratorLink[];
  resumeUrl?: string | null;
  notableProjects?: CollaboratorProjectEntry[];
  bankDetails?: CollaboratorBankDetails | null;
  uid?: string | null;
}

export const AVAILABILITY_LABEL: Record<CollaboratorAvailability, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  unavailable: 'Unavailable',
};

export const COLLABORATOR_STATUS_LABEL: Record<CollaboratorStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  offboarded: 'Archived',
};